export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-2 text-3xl font-bold">Privacy Policy</h1>
        <p className="mb-8 text-sm text-muted-foreground">Last updated: July 29, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground dark:prose-invert">
          <section>
            <p>
              Stoop, operated by <strong>MarketLink Labs LLC</strong> (&ldquo;we&rdquo;, &ldquo;our&rdquo;,
              &ldquo;us&rdquo;), is a local food marketplace that connects neighborhood buyers and sellers. This Privacy
              Policy explains what information we collect when you use the Stoop app or website, how we use it, and the
              choices you have.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account information:</strong> name, email address, phone number, profile photo, and password
                (stored hashed) when you create an account.
              </li>
              <li>
                <strong>Listings and orders:</strong> product descriptions, photos, prices, availability, and
                transaction history when you buy or sell on Stoop.
              </li>
              <li>
                <strong>Location:</strong> your approximate or precise location, when you allow it, to show you nearby
                sellers, listings, and delivery options. You can change this permission in your device settings at any
                time.
              </li>
              <li>
                <strong>Photos:</strong> images you upload from your camera or photo library to list products or update
                your profile.
              </li>
              <li>
                <strong>Payment information:</strong> we do not store full payment card numbers. Payments are processed
                by Stripe, which handles and stores card data under its own Privacy Policy.
              </li>
              <li>
                <strong>Device and usage information:</strong> device type, operating system, app version, IP address,
                push notification tokens, and how you interact with the app. This helps us diagnose issues and improve
                the product.
              </li>
              <li>
                <strong>Searches and requests:</strong> what you type into search, what results we showed you, and which
                result you opened. We use this to tell whether our search actually answered you, to improve it, and over
                time to suggest things you are more likely to want. If you ask for something nobody sells yet, we keep
                that request (and the ZIP code it came from) so we know what to bring to your neighborhood, and we let
                you know if a neighbor lists it. If you tell us about a neighbor who makes something, we keep the name
                and contact details you give us so we can invite them to sell.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve Stoop features.</li>
              <li>To match buyers and sellers, calculate delivery routes, and display nearby listings.</li>
              <li>To process orders, payments, and refunds through Stripe.</li>
              <li>To send transactional emails and push notifications about your orders, messages, and account.</li>
              <li>To detect and prevent fraud, abuse, or other violations of our terms.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Sharing</h2>
            <p>We share information only in these cases:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>With other users:</strong> when you sell or buy, the other party sees what they need to complete
                the transaction (name, listing details, delivery location, messages).
              </li>
              <li>
                <strong>With service providers:</strong> Stripe (payments), Google Cloud (hosting), Firebase (push
                notifications and crash reporting), and Resend (email delivery), under their respective privacy
                practices.
              </li>
              <li>
                <strong>For legal reasons:</strong> if required by law, court order, or to protect the rights, property,
                or safety of Stoop or its users.
              </li>
            </ul>
            <p>We do not sell your personal information.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Data Retention</h2>
            <p>
              We keep your account information for as long as your account is active. You can delete your account at any
              time from Account settings, or by emailing us. Transaction records may be retained longer where required by
              tax or other law.
            </p>
            <p>
              Search history is kept for 24 months and is deleted when you delete your account. Requests for items we
              don't carry, and maker suggestions, are kept while they are useful for bringing sellers to your area; these
              are used in aggregate and are not shown publicly with your name attached.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Your Choices</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You can update your profile information from your account settings.</li>
              <li>You can turn off location access, camera, photo, and notification permissions in your device settings.</li>
              <li>You can request a copy or deletion of your data, including your search history, by emailing us.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Security</h2>
            <p>
              We use industry-standard safeguards (HTTPS, hashed passwords, access controls) to protect your
              information. No system is perfectly secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Children</h2>
            <p>
              Stoop is not directed to children under 13, and we do not knowingly collect personal information from
              children under 13. If you believe a child has provided us with personal information, please contact us and
              we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Changes</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the &ldquo;Last
              updated&rdquo; date at the top. Continued use of Stoop after changes take effect constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">Contact</h2>
            <p>
              Questions about this policy or your data? Email{" "}
              <a href="mailto:hello@trystoop.com" className="underline">
                hello@trystoop.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
